// views/UserAccess.jsx
// Per-tenant "User Access" admin for the Events app. Shows who can reach THIS
// tenant and lets a tenant admin add/change/remove access. The source of truth
// is central Teliance Auth — this page reflects it and writes back through the
// Events backend (which holds the service credential). Email + 2FA are managed
// centrally, not here. The DISPLAY NAME is editable here: in teliance-auth the
// `username` field IS the display name (UI + token/SSO claims) — NOT a login
// credential (login is by email) and not unique, so renaming is a safe free-text edit.
import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api.js';
import { UserPlus, Trash2, ShieldCheck, Globe, Loader2, Pencil } from 'lucide-react';

const ROLES = ['admin', 'ops', 'viewer'];
// Mirror of Events NAV_GROUPS section paths (keep in sync with App.jsx).
const EVENTS_SECTIONS = [
  { path: 'list', label: 'List' },
  { path: 'calendar', label: 'Calendar' },
  { path: 'kanban', label: 'Kanban' },
  { path: 'event-dashboard', label: 'Box Office Live' },
  { path: 'ticketing-widget', label: 'Ticketing Widget' },
  { path: 'meta-audiences', label: 'Meta audiences' },
  { path: 'facebook-pages', label: 'Pages' },
  { path: 'meta-performance', label: 'Meta performance' },
  { path: 'ref-codes', label: 'Ref codes' },
  { path: 'url-generator', label: 'URL generator' },
  { path: 'universe', label: 'Universe' },
  { path: 'urvenue', label: 'UrVenue' },
  { path: 'bandsintown', label: 'Bandsintown' },
  { path: 'resident-advisor', label: 'Resident Advisor' },
  { path: 'ticketweb', label: 'TicketWeb' },
  { path: 'templates', label: 'Templates' },
  { path: 'event-dashboard-config', label: 'Box Office Live config' },
  { path: 'activity', label: 'Activity' },
  { path: 'user-access', label: 'User Access' },
  { path: 'settings', label: 'Settings' },
];
const ALL_SECTION_PATHS = EVENTS_SECTIONS.map((s) => s.path);
const COLORS = [
  'bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500', 'bg-teal-500',
  'bg-sky-500', 'bg-indigo-500', 'bg-violet-500', 'bg-fuchsia-500', 'bg-pink-500',
];
function avatarColor(seed) {
  const s = String(seed || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return COLORS[h % COLORS.length];
}
function initialsOf(name, email) {
  return ((name || email || '?').split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join('') || '?').toUpperCase();
}

export default function UserAccess() {
  const { tenantSlug } = useParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  // Section access chosen at ADD time (all checked = unrestricted).
  const [addSecs, setAddSecs] = useState(ALL_SECTION_PATHS); // optional display name for a NEW invite
  const [role, setRole] = useState('viewer');
  const [nameOpen, setNameOpen] = useState(null); // membership_id whose name editor is open
  const [nameDraft, setNameDraft] = useState('');
  const [nameBusy, setNameBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [tenantName, setTenantName] = useState(tenantSlug);
  const [secOpen, setSecOpen] = useState(null); // membership_id whose section editor is open
  const [secDraft, setSecDraft] = useState([]); // working section selection
  const [secBusy, setSecBusy] = useState(false);

  // Resolve the venue's display name (same source as the header switcher) so
  // the page reads "LIV Nightclub Miami", not the raw slug.
  useEffect(() => {
    setTenantName(tenantSlug);
    apiFetch('/api/me/tenants')
      .then((r) => r.json())
      .then((d) => {
        const t = (d.tenants || []).find((x) => x.slug === tenantSlug);
        if (t?.name) setTenantName(t.name);
      })
      .catch(() => {});
  }, [tenantSlug]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/access`);
      if (r.status === 403) {
        setUsers([]);
        setError('You need admin access to this tenant to manage users.');
        return;
      }
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `Error ${r.status}`);
      setUsers(d.users || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/access`, {
        method: 'POST', body: JSON.stringify({ email: email.trim(), role, name: name.trim(), sections: addSecs.length === ALL_SECTION_PATHS.length ? [] : addSecs }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || d.detail || `Error ${r.status}`);
      setNotice(d.invited
        ? `Invited ${email.trim()}. A set-password email was sent.`
        : d.resent
          ? `Added ${email.trim()}. They hadn't activated yet, so the invite email was re-sent.`
          : `Added ${email.trim()}.`);
      setEmail('');
      setName('');
      setAddSecs(ALL_SECTION_PATHS);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (u, newRole) => {
    setError(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/access/${u.user_id}`, {
        method: 'PATCH', body: JSON.stringify({ role: newRole }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `Error ${r.status}`); }
      setUsers((us) => us.map((x) => (x.membership_id === u.membership_id ? { ...x, role: newRole } : x)));
    } catch (e) {
      setError(e.message);
      load();
    }
  };

  // Rename a user's display name. PATCHes the central `username` (free-text, not a
  // login credential, not unique) for brand members of THIS tenant.
  const openName = (u) => { setNameDraft(u.username || ''); setNameOpen(u.membership_id); };
  const saveName = async (u) => {
    const nm = nameDraft.trim();
    if (!nm) return;
    setNameBusy(true);
    setError(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/access/${u.user_id}/profile`, {
        method: 'PATCH', body: JSON.stringify({ name: nm }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `Error ${r.status}`); }
      setNameOpen(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setNameBusy(false);
    }
  };

  const sectionsOf = (u) => (Array.isArray(u.capabilities?.sections) && u.capabilities.sections.length
    ? u.capabilities.sections : null); // null = all
  const openSections = (u) => {
    setSecDraft(sectionsOf(u) || ALL_SECTION_PATHS);
    setSecOpen(u.membership_id);
  };
  const toggleSec = (path) => setSecDraft((d) => (d.includes(path) ? d.filter((p) => p !== path) : [...d, path]));
  const saveSections = async (u) => {
    setSecBusy(true);
    setError(null);
    // all selected ⇒ send [] (no restriction = all)
    const sections = secDraft.length === ALL_SECTION_PATHS.length ? [] : secDraft;
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/access/${u.user_id}/sections`, {
        method: 'PATCH', body: JSON.stringify({ sections }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `Error ${r.status}`); }
      setSecOpen(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSecBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email} from ${tenantName}?`)) return;
    setError(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/access/${u.membership_id}`, { method: 'DELETE' });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `Error ${r.status}`); }
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-semibold mb-1">User Access</h1>
      <p className="text-sm text-muted-foreground mb-5">
        People who can access <span className="font-medium">{tenantName}</span>.
      </p>

      <form onSubmit={add} className="flex flex-wrap items-end gap-2 mb-5 p-4 border border-border rounded-lg bg-card">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs text-muted-foreground mb-1">Name</label>
          <input
            type="text" required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            className="w-full h-9 px-3 text-sm border border-input rounded-md bg-background"
          />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-muted-foreground mb-1">Email</label>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="person@company.com"
            pattern="[^\s@]+@[^\s@]+\.[A-Za-z]{2,}" title="Full email address including the domain, e.g. name@company.com"
            className="w-full h-9 px-3 text-sm border border-input rounded-md bg-background"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}
            className="h-9 px-2 text-sm border border-input rounded-md bg-background capitalize">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <button type="submit" disabled={busy}
          className="h-9 px-3 inline-flex items-center gap-1.5 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Add
        </button>
        <div className="w-full pt-1">
          <div className="text-xs text-muted-foreground mb-1.5">Section access <span className="opacity-60">(all checked = full access)</span></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5">
            {EVENTS_SECTIONS.map((s) => (
              <label key={s.path} className="flex items-center gap-1.5 text-xs">
                <input type="checkbox" checked={addSecs.includes(s.path)}
                  onChange={() => setAddSecs((cur) => cur.includes(s.path) ? cur.filter((x) => x !== s.path) : [...cur, s.path])} />
                {s.label}
              </label>
            ))}
          </div>
        </div>
      </form>

      {notice && <div className="mb-3 text-sm text-emerald-600">{notice}</div>}
      {error && <div className="mb-3 text-sm text-destructive">{error}</div>}

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : users.length === 0 && !error ? (
        <div className="text-muted-foreground">No users have access yet.</div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden divide-y divide-border">
          {users.map((u) => {
            const editable = u.via === 'brand';
            const secs = sectionsOf(u);
            return (
              <div key={u.membership_id} className="bg-card">
                <div className="flex items-center gap-3 px-3 py-2.5">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white ${avatarColor(u.email || u.username)}`}>
                  {initialsOf(u.username, u.email)}
                </div>
                <div className="min-w-0 flex-1">
                  {editable && nameOpen === u.membership_id ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') saveName(u); if (e.key === 'Escape') setNameOpen(null); }}
                        placeholder="Display name"
                        className="h-7 px-2 text-sm border border-input rounded-md bg-background min-w-0 flex-1"
                      />
                      <button onClick={() => saveName(u)} disabled={nameBusy || !nameDraft.trim()}
                        className="h-7 px-2 text-xs rounded-md bg-primary text-primary-foreground disabled:opacity-50">
                        {nameBusy ? '…' : 'Save'}
                      </button>
                      <button onClick={() => setNameOpen(null)} className="h-7 px-2 text-xs rounded-md border border-input">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium truncate">{u.username || u.email}</span>
                      {editable && (
                        <button onClick={() => openName(u)} title="Edit name"
                          className="shrink-0 text-muted-foreground hover:text-foreground">
                          <Pencil size={12} />
                        </button>
                      )}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                {u.via === 'org' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-muted-foreground inline-flex items-center gap-1">
                    <ShieldCheck size={11} />via org
                  </span>
                )}
                {u.via === 'global' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-muted-foreground inline-flex items-center gap-1">
                    <Globe size={11} />global
                  </span>
                )}
                {editable && (
                  <button onClick={() => (secOpen === u.membership_id ? setSecOpen(null) : openSections(u))}
                    className="text-xs h-8 px-2 rounded-md border border-input hover:bg-accent whitespace-nowrap">
                    {secs ? `Sections: ${secs.length}` : 'Sections: All'}
                  </button>
                )}
                {editable ? (
                  <select value={u.role} onChange={(e) => changeRole(u, e.target.value)}
                    className="h-8 px-2 text-xs border border-input rounded-md bg-background capitalize">
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : (
                  <span className="text-xs text-muted-foreground capitalize w-[68px] text-center">{u.role}</span>
                )}
                <button
                  onClick={() => editable && remove(u)} disabled={!editable}
                  title={editable ? 'Remove' : 'Managed at org/global level'}
                  className={`p-1.5 rounded-md ${editable ? 'text-muted-foreground hover:text-destructive hover:bg-accent' : 'opacity-30 cursor-not-allowed'}`}>
                  <Trash2 size={15} />
                </button>
                </div>
                {editable && secOpen === u.membership_id && (
                  <div className="px-3 pb-3 pt-1 border-t border-border bg-muted/20">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5 my-2">
                      {EVENTS_SECTIONS.map((s) => (
                        <label key={s.path} className="flex items-center gap-1.5 text-xs">
                          <input type="checkbox" checked={secDraft.includes(s.path)} onChange={() => toggleSec(s.path)} />
                          {s.label}
                        </label>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => saveSections(u)} disabled={secBusy}
                        className="h-7 px-3 text-xs rounded-md bg-primary text-primary-foreground disabled:opacity-50">
                        {secBusy ? 'Saving…' : 'Save sections'}
                      </button>
                      <button onClick={() => setSecOpen(null)} className="h-7 px-3 text-xs rounded-md border border-input">Cancel</button>
                      <span className="text-[11px] text-muted-foreground">All checked = full access</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
